import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AdminLayout from '../layout/AdminLayout';
import {
  getReviewPermission,
  getReviewQueue,
  getOptimizerQueue,
  advanceBot,
  adminEditReview,
  verifyProvider,
  ReviewBot,
  OptimizerRunSummary,
} from '../../services/providerReviewService';
import BotTable from './BotTable';
import './BotReviewPage.css';

// One-step-forward review path + reject (mirror of the backend state machine).
const REVIEW_ACTIONS: Record<string, string[]> = {
  Submitted: ['InReview', 'Draft'],
  InReview: ['ReadyForTesting', 'Draft'],
  ReadyForTesting: ['Testing', 'Draft'],
  Testing: ['Published', 'Draft'],
};

const BotReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [queue, setQueue] = useState<ReviewBot[]>([]);
  const [optimizing, setOptimizing] = useState<OptimizerRunSummary[]>([]);
  const [awaitingReview, setAwaitingReview] = useState<OptimizerRunSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setQueue(await getReviewQueue());
      // Optimizer tabs (design §9) — absent/unwired environments return empty.
      const opt = await getOptimizerQueue().catch(() => ({ optimizing: [], awaitingReview: [] }));
      setOptimizing(opt.optimizing);
      setAwaitingReview(opt.awaitingReview);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getReviewPermission().then((ok) => {
      setAllowed(ok);
      if (ok) load();
    });
  }, [load]);

  const act = async (fn: () => Promise<void>, key: string) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  if (allowed === null) {
    return <AdminLayout><div className="review-wrap">…</div></AdminLayout>;
  }
  if (!allowed) {
    return (
      <AdminLayout>
        <div className="review-wrap">
          <h2>{t('review.title', 'Bot Review')}</h2>
          <p className="review-denied">{t('review.denied', 'You do not have bot-review permission.')}</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="review-wrap">
        <div className="review-head">
          <div>
            <h2>{t('review.title', 'Bot Review')}</h2>
            <p className="review-sub">{t('review.subtitle', 'Bots awaiting review. Approved bots move to All Bots.')}</p>
          </div>
          <button className="review-refresh" onClick={load} disabled={loading}>
            {t('review.refresh', 'Refresh')}
          </button>
        </div>
        {error && <div className="review-error">{error}</div>}
        {(optimizing.length > 0 || awaitingReview.length > 0) && (
          <div className="opt-queue-strip">
            <div className="opt-queue-col">
              <h3>{t('review.optimizer.optimizing', 'Optimizing')} ({optimizing.length})</h3>
              {optimizing.map((r) => (
                <div key={r.botId! + r.runTs} className="opt-queue-row">
                  <span className={`opt-status opt-status-${r.status}`}>{t(`review.optimizer.status.${r.status}`, r.status)}</span>
                  <b>{r.displayName || r.botId}</b>
                  <span className="detail-muted">{t(`review.optimizer.trigger.${r.trigger}`, r.trigger)}</span>
                </div>
              ))}
              {optimizing.length === 0 && <p className="detail-muted">—</p>}
            </div>
            <div className="opt-queue-col">
              <h3>{t('review.optimizer.awaiting', 'Awaiting approval')} ({awaitingReview.length})</h3>
              {awaitingReview.map((r) => (
                <div key={r.botId! + r.runTs} className="opt-queue-row">
                  <b>{r.displayName || r.botId}</b>
                  {r.evalSummary && (
                    <span className="opt-eval-badges">
                      <span className="opt-grade-good">✓{r.evalSummary.good}</span>
                      <span className="opt-grade-partial">△{r.evalSummary.partial}</span>
                      <span className="opt-grade-poor">✗{r.evalSummary.poor}</span>
                    </span>
                  )}
                  <span className="detail-muted">{t('review.optimizer.openHint', 'open the bot below for the full run log')}</span>
                </div>
              ))}
              {awaitingReview.length === 0 && <p className="detail-muted">—</p>}
            </div>
          </div>
        )}
        {loading ? (
          <p>…</p>
        ) : queue.length === 0 ? (
          <div className="review-empty-state">
            <p>{t('review.empty', 'No bots are waiting for review.')}</p>
            <Link to="/all-bots">{t('review.emptyLink', 'View all bots →')}</Link>
          </div>
        ) : (
          <BotTable
            bots={queue}
            actionsFor={(s) => REVIEW_ACTIONS[s] || []}
            busy={busy}
            onAction={(botId, to, note, reasons) => act(() => advanceBot(botId, to, note, reasons), botId + to)}
            onVerify={(providerId) => act(() => verifyProvider(providerId), 'v' + providerId)}
            onEditReview={(botId, approve, note, reasons) => act(() => adminEditReview(botId, approve, note, reasons), botId + (approve ? 'editApprove' : '__rejectEdit'))}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default BotReviewPage;
